import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { EditorEligibility, EditorEligibilityDialogComponent } from '@fry/booking/eligibility-editor';

import { EditorBookingItem, SETTING_ELIGIBILITY_INDEX } from '../editor-booking-item/editor-booking-item.model';
import { EditorBookingItemSettingEligibility } from '../editor-booking-item-setting/editor-booking-item-setting-eligibility.model';
import { EditorBookingItemTreeStore } from '../editor-booking-item-tree/editor-booking-item-tree.store';


@Component({
    selector: 'eas-editor-eligibility-editor-loader',
    templateUrl: './editor-eligibility-editor-loader.component.html',
    styleUrls: ['./editor-eligibility-editor-loader.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditorEligibilityEditorLoaderComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  @Input() eligibilityId: string;

  @Output() set = new EventEmitter<{ id: string }>();
  @Output() unset = new EventEmitter<void>();

  itemList: EditorBookingItem[];
  eligibilityList: EditorEligibility[];

  constructor(
    private dialog: MatDialog,
    private itemTreeStore: EditorBookingItemTreeStore
  ) {

  }

  ngOnInit() {
    this.itemTreeStore.bookingItem$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(bookingItem => this.eligibilityList = bookingItem.doc.eligibilities);

    this.itemTreeStore.itemTree$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(itemTree => this.itemList = itemTree.itemList);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private openEligibilityEditor(action: 'create'|'edit'|'duplicate', eligibilityId: string) {
    const data = {
      action,
      eligibility: action === 'create'
        ? undefined
        : this.eligibilityList.find(eligibility => eligibility.id === eligibilityId),
      assignedBookingItemNameList: action === 'edit'
        ? this.itemList
          .filter(
            item => (item.settings[SETTING_ELIGIBILITY_INDEX] as EditorBookingItemSettingEligibility).id === this.eligibilityId
          ).map(
            item => item.name
          )
        : []
    };

    const dialogRef = this.dialog.open(EditorEligibilityDialogComponent, {
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      height: '100%',
      width: '100%',
      maxWidth: '90vw',
      panelClass: 'mat-typography',
      id: 'booking-item-editor-eligibility-dialog',
      data
    });

    dialogRef.afterClosed().subscribe(eligibilityWithAction => {
      if (eligibilityWithAction === undefined) {
        return;
      }

      switch (eligibilityWithAction.action) {
        case 'create':
          this.itemTreeStore.addEligibility(eligibilityWithAction.eligibility);
          this.set.emit({ id: eligibilityWithAction.eligibility.id });
          break;
        case 'edit':
          this.itemTreeStore.replaceEligibility(eligibilityId, eligibilityWithAction.eligibility);
          break;
        case 'delete':
          this.unset.emit();
          this.itemTreeStore.deleteEligibility(eligibilityId);
          break;
      }
    });
  }

  onCreate() {
    this.openEligibilityEditor('create', undefined);
    return false;
  }

  onEdit(eligibilityId: string) {
    this.openEligibilityEditor('edit', eligibilityId);
    return false;
  }

  onDuplicate(eligibilityId: string) {
    this.openEligibilityEditor('duplicate', eligibilityId);
    return false;
  }
}
